import { Button, Tooltip, message } from "antd";
import { fetchClients, roleSelector } from "features/jobinv/JobCardSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CardStyle } from "../style/componentsStyle";
import CardsTable from "components/MovieListing/CardsTable";
import { ROLES, STAGES } from "components/common/Constants";
import styled from "styled-components";
import Loader from "components/common/Loader";
import ShowCount from "./ShowCount";
import {
  useFetchJobcardsCompletedQuery,
  useFetchJobcardsQuery,
  useFetchTopCardCountQuery,
  useGetClientsQuery,
  useGetCompanyNameFromJobcardCompletedQuery,
  useGetCompanyNameFromJobcardQuery,
  useGetJobcardQuery,
} from "features/store";
import MultidropDown from "components/MovieListing/MultidropDown";
import AscDesc from "components/MovieListing/AscDesc";
import CrossButton from "atoms/CrossButton";
import {
  getManufatureDataSet,
  getSum,
  getUniqueColors,
  formatHatDataToJSON,
} from "components/State";
import { useReactToPrint } from "react-to-print";
import PriorityInfo from "./PriorityInfo";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { getFormalName } from "components/services";
import moment from "moment";
import { getColorHex, extractColorName } from "../../utils/identifyColor";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const ItemMain = ({ lastData }) => {
  //  console.log("lastData", lastData);
  return <>{getSum(lastData)}</>;
};

const Cap = ({ material_types, st }) => {
  // console.log("material_types 44", material_types);
  const uniqueKeys = Object.keys(material_types);

  return (
    <div style={st}>
      {uniqueKeys?.map((item) => (
        <>
          <h3 style={{ color: "black", textAlign: "center" }}>
            <span style={{ fontWeight: "900", letterSpacing: "2px" }}>
            </span>
            <ItemMain lastData={material_types[item]} />
          </h3>
        </>
      ))}
    </div>
  );
};

const CapList = ({ caps }) => {
  // console.log("33", caps);
  const uniqueKeys = Object.keys(caps);

  return (
    <div style={{ display: "flex", color: "white" }}>
      {Object.keys(caps).map((side) => (
        <div
          style={{
            margin: "10px 0px",
            marginRight: "10px",
            padding: "10px 20px",
            background: "var(--color-primary--light)",
          }}
        >
          <h4 style={{ color: "black" }}>{side} </h4>
          <Cap material_types={caps[side]} />
        </div>
      ))}
    </div>
  );
};

function ClothMaterial({ clothMaterial }) {
  // console.log("clothMaterial 22", clothMaterial);

  return (
    <div>
      {clothMaterial["CLOTH MATERIAL"] ? (
        <>
          <CapList caps={clothMaterial["CLOTH MATERIAL"]} />
        </>
      ) : null}
    </div>
  );
}

function MaterialTypes({ material_types }) {
  return (
    <div>
      <ClothMaterial clothMaterial={material_types?.material_types} />
    </div>
  );
}

// New components that render directly from the formatted JSON
// { styles: [ { style, colors: [ { color, total, parts: [ { name, quantity } ] } ] } ] }

function ColorGroup({ colorData }) {
  if (!colorData) return null;

  const { color, total, parts } = colorData;
  const safeParts = Array.isArray(parts) ? parts : [];
  const colorTotal =
    typeof total === "number"
      ? total
      : safeParts.reduce((acc, p) => acc + (Number(p.quantity) || 0), 0);

  if (!safeParts.length) return null;

  return (
    <ColorCard>
      <ColorHeader>
        <ColorName>{color}</ColorName>
        <ColorBadge>
          <ColorBadgeLabel>Total</ColorBadgeLabel>
          <ColorBadgeValue>{colorTotal || 0} pcs</ColorBadgeValue>
        </ColorBadge>
      </ColorHeader>
      <SideList>
        {safeParts.map((part, index) => (
          <SideItem key={`${color}-${part.name}-${index}`}>
            <SideLabel>{part.name}</SideLabel>
            <SideQuantity>{part.quantity}</SideQuantity>
          </SideItem>
        ))}
      </SideList>
    </ColorCard>
  );
}

function ColorGroups({ colors }) {
  if (!colors || !Array.isArray(colors) || colors.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>No color data available</h3>
      </div>
    );
  }

  return (
    <ColorGrid>
      {colors
        .filter((c) => c && c.color != null && c.color !== "")
        .map((c, index) => (
          <ColorGroup key={`${c.color}-${index}`} colorData={c} />
        ))}
    </ColorGrid>
  );
}

function StyleGroups({ styles }) {
  if (!styles || styles.length === 0) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>No style data available</h3>
      </div>
    );
  }

  return (
    <div>
      {styles.map((styleItem) => {
        const styleName = styleItem?.style || "Unknown Style";
        const colors = styleItem?.colors || [];

        if (!colors.length) return null;

        return (
          <StyleCard key={styleName}>
            <StyleHeader>
              <div>
                <StyleLabel>Style</StyleLabel>
                <StyleTitle>{styleName}</StyleTitle>
              </div>
            </StyleHeader>
            <ColorGroups colors={colors} />
          </StyleCard>
        );
      })}
    </div>
  );
}

// extractColorName is now imported from utils/identifyColor

// Helper function to extract color code from strings like "FX1 NAVY" or "FX2 MAROON"
const extractColorCode = (colorString) => {
  if (!colorString || typeof colorString !== 'string') return '';

  // Extract the code part (letters and numbers before the first space)
  const match = colorString.match(/^([A-Z0-9]+)/i);
  return match ? match[1].toUpperCase() : '';
};

// Helper function to sort colors: FX2 MAROON first, then others by color code ascending
const sortColorsByCode = (colors) => {
  if (!Array.isArray(colors)) return colors;

  const sorted = [...colors].sort((a, b) => {
    const colorA = String(a?.color || '').toUpperCase();
    const colorB = String(b?.color || '').toUpperCase();

    // FX2 MAROON should always be first
    if (colorA === 'FX2 MAROON') return -1;
    if (colorB === 'FX2 MAROON') return 1;

    // Extract color codes
    const codeA = extractColorCode(colorA);
    const codeB = extractColorCode(colorB);

    // If both have codes, sort by code (numeric-aware)
    if (codeA && codeB) {
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    }

    // If only one has a code, prioritize it
    if (codeA && !codeB) return -1;
    if (!codeA && codeB) return 1;

    // If neither has a code, sort alphabetically
    return colorA.localeCompare(colorB);
  });

  return sorted;
};

// Table-based view for hat inventory inside the generate modal
// Data shape: { styles: [ { style, colors: [ { color, total, parts: [ { name, quantity } ] } ] } ] }
const HatInventoryView = ({ styles, selectedQty }) => {
  if (!styles || !styles.length) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>No data available</h3>
      </div>
    );
  }

  const getColorClass = (colorName) => {
    // Normalize color name for safer matching
    const c = String(colorName || "").toLowerCase();

    if (c.includes("red")) return "color-red";
    if (c.includes("blue")) return "color-blue";
    if (c.includes("orange")) return "color-orange";
    if (c.includes("green")) return "color-green";

    // Newly handled colors
    if (c.includes("black")) return "color-black";
    if (c.includes("yellow") || c.includes("gold")) return "color-yellow";
    if (c.includes("pink")) return "color-pink";

    // Fallback for any unknown color
    return "color-default";
  };

  return (
    <div className="hat-inventory-container">
      <div className="hat-inventory-title-container">
        <h1 className="hat-inventory-title">Cap Color Quantity</h1>
        {selectedQty !== undefined && selectedQty !== null && (
          <span className="selected-qty-badge">Selected Qty: {selectedQty}</span>
        )}
      </div>

      {styles.map((styleItem, styleIdx) => {
        const styleName = styleItem?.style || `Style ${styleIdx + 1}`;
        const colors = Array.isArray(styleItem?.colors)
          ? styleItem.colors.filter((c) => c && c.color)
          : [];

        if (!colors.length) return null;

        // Sort colors: FX2 MAROON first, then others by color code ascending
        const sortedColors = sortColorsByCode(colors);

        return (
          <div className="style-section" key={`${styleName}-${styleIdx}`}>
            <div className="style-title">{styleName}</div>

            <table className="hat-table">
              <thead>
                <tr>
                  <th>Color</th>
                  <th>Part</th>
                  <th>Quantity</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {sortedColors.map((colorGroup, colorIdx) => {
                  if (!colorGroup) return null;
                  const { color, total, parts } = colorGroup;
                  const safeParts = Array.isArray(parts) ? parts : [];

                  if (!safeParts.length) return null;

                  const colorTotal =
                    typeof total === "number"
                      ? total
                      : safeParts.reduce(
                        (acc, p) => acc + (Number(p.quantity) || 0),
                        0
                      );

                  const rowSpan = safeParts.length;

                  // Get hex color prioritizing color_code if available in colorGroup
                  const colorData = colorGroup?.color_code ? { color_code: colorGroup.color_code, color: color } : { color: color };
                  const hexColor = getColorHex(colorData);
                  const isWhite = hexColor?.toLowerCase() === '#ffffff' || hexColor?.toLowerCase() === '#fff';

                  return (
                    <React.Fragment
                      key={`${styleName}-${color}-${colorIdx}`}
                    >
                      {safeParts.map((part, partIdx) => (
                        <tr
                          key={`${styleName}-${color}-${part.name}-${partIdx}`}
                          className={
                            partIdx === safeParts.length - 1
                              ? "color-last-row"
                              : ""
                          }
                        >
                          {partIdx === 0 && (
                            <td
                              rowSpan={rowSpan}
                              className="color-cell color-header"
                            >
                              <span
                                className="color-badge"
                                style={{
                                  backgroundColor: hexColor || '#6b7280',
                                  color: isWhite ? '#1f2933' : '#ffffff',
                                  border: isWhite ? '1px solid #d9d9d9' : 'none',
                                }}
                              >
                                {color}
                              </span>
                            </td>
                          )}
                          <td>{part?.name || "-"}</td>
                          <td>
                            {part?.quantity ? `${part.quantity} pcs` : "-"}
                          </td>
                          {partIdx === 0 && (
                            <td rowSpan={rowSpan} className="total-row">
                              {colorTotal || 0} pcs
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

const GenerateModel = ({ performCancel, handlePrint, componentRef, stylesData, selectedQty }) => {
  return (
    <div className="model-con">
      <div className="model-box" style={{ width: "60vw", padding: "0px" }}>
        <CrossButton performCancel={performCancel} />
        <div
          style={{
            textAlign: "center",
            position: "absolute",
            display: "flex",
            alignItem: "center",
            justifyContent: "center",
            top: "-36px",
          }}
        >
          <button
            style={{
              textAlign: "center",
              background: "var(--color-primary)",
              color: "var(--light)",
              padding: ".5rem 1rem",
              border: "none",
              borderRadius: ".2rem",
              fontWeight: "500",
              cursor: "pointer",
            }}
            onClick={handlePrint}
          >
            Print Out
          </button>
        </div>
        <div className="model-box2">
          <div className="dummy"></div>
          <div className="test" ref={componentRef}>
            <HatInventoryView styles={stylesData} selectedQty={selectedQty} />
          </div>
        </div>
      </div>
    </div>
  );
};

const getFormalNameSafe = (stage) => {
  return getFormalName ? getFormalName(stage) : stage;
};

const prepareCSVData = (dataSource, clients) => {
  if (!dataSource || dataSource.length === 0) return [];

  return dataSource.map((card) => {
    const formattedStatus = card.status
      ? card.status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : 'Pending';

    // Format stage date/time columns
    const formatStageDateTime = (date, time) => {
      if (!date && !time) return "";
      const formattedTime = time ? moment(time, "HH:mm:ss").format("HH:mm") : "";
      return date && formattedTime ? `${date} ${formattedTime}` : date || formattedTime;
    };

    const client = clients?.find((c) => c.id === card.client);

    return {
      "Job Card No.": card.serial_number || card.id,
      "Client Company": card.client_company || client?.company_name || "",
      "Date": card.date || "",
      "Deliver Date": card.deliver_date || "",
      "Quantity": card.quantity || 0,
      "Created By": card.Created_by || "",
      "Current Stage": card.current_stage
        ? getFormalNameSafe(card.current_stage)
        : (card.is_degitizer_stage ? "Revision" : ""),
      "Payment Status": formattedStatus,
      "Designer": formatStageDateTime(card.designer_update_date, card.designer_update_time),
      "Cutting": formatStageDateTime(card.cutting_update_date, card.cutting_update_time),
      "Secondary Process": formatStageDateTime(card.secendory_update_date, card.secendory_update_time),
      "Embroidary": formatStageDateTime(card.emboridary_update_date, card.emboridary_update_time),
      "Production": formatStageDateTime(card.production_update_date, card.production_update_time),
      "Packing": formatStageDateTime(card.packing_update_date, card.packing_update_time),
      "Billing": formatStageDateTime(card.accountent_update_date, card.accountent_update_time),
      "Payment": formatStageDateTime(card.payment_update_date, card.payment_update_time),
      "Dispatch": formatStageDateTime(card.dispatch_update_date, card.dispatch_update_time),
      "Del.Address": card.shipping_address || "",
      "Co.Mode": card.courier_mode || "",
      "Customer Name": client?.company_name || "",
      "Cash": client?.cash || "",
      "GST": client?.GST_NO || "",
      "Rate": card.rate || "",
      "Style": card.style || "",
    };
  });
};

const csvHeaders = [
  { label: "Job Card No.", key: "Job Card No." },
  { label: "Client Company", key: "Client Company" },
  { label: "Date", key: "Date" },
  { label: "Deliver Date", key: "Deliver Date" },
  { label: "Quantity", key: "Quantity" },
  { label: "Created By", key: "Created By" },
  { label: "Current Stage", key: "Current Stage" },
  { label: "Payment Status", key: "Payment Status" },
  { label: "Designer", key: "Designer" },
  { label: "Cutting", key: "Cutting" },
  { label: "Secondary Process", key: "Secondary Process" },
  { label: "Embroidary", key: "Embroidary" },
  { label: "Production", key: "Production" },
  { label: "Packing", key: "Packing" },
  { label: "Billing", key: "Billing" },
  { label: "Payment", key: "Payment" },
  { label: "Dispatch", key: "Dispatch" },
  { label: "Del.Address", key: "Del.Address" },
  { label: "Co.Mode", key: "Co.Mode" },
  { label: "Customer Name", key: "Customer Name" },
  { label: "Cash", key: "Cash" },
  { label: "GST", key: "GST" },
  { label: "Rate", key: "Rate" },
  { label: "Style", key: "Style" },
];

const CardListing = () => {
  const componentRef = useRef();
  const csvLinkUnderProcessRef = useRef(null);
  const csvLinkCompletedRef = useRef(null);
  const { data: clients } = useGetClientsQuery();

  const handleExportUnderProcess = () => {
    if (csvLinkUnderProcessRef.current) {
      const linkElement = csvLinkUnderProcessRef.current.link || csvLinkUnderProcessRef.current;
      if (linkElement && typeof linkElement.click === 'function') {
        linkElement.click();
      }
    }
  };

  const handleExportCompleted = () => {
    if (csvLinkCompletedRef.current) {
      const linkElement = csvLinkCompletedRef.current.link || csvLinkCompletedRef.current;
      if (linkElement && typeof linkElement.click === 'function') {
        linkElement.click();
      }
    }
  };

  const handlePrint = useReactToPrint({
    copyStyles: true,
    content: () => componentRef.current,
    documentTitle: "emp-data",
    // Ensure the print view has the same table styling
    pageStyle: `
      @page {
        margin: 20mm;
      }
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        background: #f5f5f5;
      }
      .hat-inventory-container {
        max-width: 100%;
        padding: 10px 14px 22px;
        background-color: #f5f5f5;
      }
      .hat-inventory-title-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
      }
      .hat-inventory-title {
        text-align: center;
        margin: 0;
        color: #333333;
        font-size: 24px;
        font-weight: 600;
        flex: 1;
      }
      .selected-qty-badge {
        background-color: var(--color-primary, #233872);
        color: var(--light, #ffffff);
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 600;
        white-space: nowrap;
        margin-left: 16px;
      }
      .hat-inventory-container .style-section {
        background: #ffffff;
        border-radius: 8px;
        padding: 16px 20px 20px;
        margin-bottom: 20px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      }
      .hat-inventory-container .style-title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 16px;
        color: #2c3e50;
        text-transform: capitalize;
      }
      .hat-inventory-container .hat-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 8px;
        background-color: #ffffff;
      }
      .hat-inventory-container .hat-table th {
        background-color: #3498db;
        color: #ffffff;
        padding: 10px 12px;
        text-align: left;
        font-weight: 600;
        font-size: 14px;
      }
      .hat-inventory-container .hat-table td {
        padding: 8px 12px;
        border-bottom: 1px solid #e2e8f0;
        font-size: 14px;
      }
      .hat-inventory-container .color-header {
        background-color: #ecf0f1;
        font-weight: 600;
        color: #2c3e50;
      }
      .hat-inventory-container .color-last-row td {
        border-bottom: 2px solid #3498db;
      }
      .hat-inventory-container .total-row {
        font-weight: 700;
        background-color: #e8f4f8;
        white-space: nowrap;
      }
      .hat-inventory-container .color-badge {
        display: inline-block;
        padding: 4px 10px;
        border-radius: 999px;
        color: #ffffff;
        font-weight: 600;
        font-size: 13px;
        text-transform: capitalize;
      }
      .hat-inventory-container .color-red { background-color: #e74c3c; }
      .hat-inventory-container .color-blue { background-color: #3498db; }
      .hat-inventory-container .color-orange { background-color: #e67e22; }
      .hat-inventory-container .color-green { background-color: #27ae60; }
      .hat-inventory-container .color-black { background-color: #111827; }
      .hat-inventory-container .color-yellow {
        background-color: #facc15;
        color: #1f2933;
      }
      .hat-inventory-container .color-pink { background-color: #ec4899; }
      .hat-inventory-container .color-default { background-color: #6b7280; }
    `,
    onAfterPrint: () => alert("Print success"),
  });

  const role = useSelector(roleSelector);
  const [underProcessPageSize, setUnderProcessPageSize] = useState(10);
  const [completedPageSize, setCompletedPageSize] = useState(10);
  const prevCompletedPageSizeRef = useRef(10);
  const prevUnderProcessPageSizeRef = useRef(10);
  const {
    jobcard_page,
    jobcard_page_complete,
    client_filter,
    stage_filter,
    jobid_filter,
    jobid_filter_completed,
    client_filter_completed,
    sort_job,
    sort_job_completed,
    user,
    created_by,
    date,
    embridary_placement_front_filter,
    embridary_placement_front_filter_completed,
  } = useSelector((state) => state.user);
  // console.log("user", user);

  const {
    data: cards,
    isLoading: loading,
    isFetching: fetchJob,
    refetch: refetchJobcards,
  } = useFetchJobcardsQuery({
    refetchOnMountOrArgChange: true,
    page: jobcard_page,
    page_size: underProcessPageSize,
    client_filter: client_filter,
    stage_filter: stage_filter,
    jobid_filter: jobid_filter,
    sort: sort_job,
    created_by: created_by,
    date: date,
    embridary_placement_front_filter,
  });

  const {
    data: cardsCompleted,
    isFetching: loadingCompleted,
    refetch: refetchCompletedCards,
  } = useFetchJobcardsCompletedQuery({
    refetchOnMountOrArgChange: true,
    page: jobcard_page_complete,
    page_size: completedPageSize,
    client_filter: client_filter_completed,
    jobid_filter: jobid_filter_completed,
    // Completed table: frontend sorting is used, but we still pass sort to backend for consistency
    // Default is "id" (ascending job card number)
    sort: sort_job_completed || "id",
    embridary_placement_front_filter_completed,
  });

  // Expand Completed table to all results when embroidery-front search is active
  useEffect(() => {
    const hasEmbFrontFilter = Boolean(
      embridary_placement_front_filter_completed && String(embridary_placement_front_filter_completed).trim() !== ""
    );

    if (hasEmbFrontFilter) {
      if (completedPageSize !== 10000) {
        prevCompletedPageSizeRef.current = completedPageSize || 10;
        setCompletedPageSize(10000);
      }
    } else {
      const restore = prevCompletedPageSizeRef.current || 10;
      if (completedPageSize !== restore) {
        setCompletedPageSize(restore);
      }
    }
    // Only react to filter changes; do not reset on pageSize changes
    // This prevents immediate override back to 10 after user selects 100
  }, [embridary_placement_front_filter_completed]);

  // Expand Under Processing table to all results when embroidery-front search is active
  useEffect(() => {
    const hasEmbFrontFilterUnder = Boolean(
      embridary_placement_front_filter && String(embridary_placement_front_filter).trim() !== ""
    );

    if (hasEmbFrontFilterUnder) {
      if (underProcessPageSize !== 10000) {
        prevUnderProcessPageSizeRef.current = underProcessPageSize || 10;
        setUnderProcessPageSize(10000);
      }
    } else {
      const restore = prevUnderProcessPageSizeRef.current || 10;
      if (underProcessPageSize !== restore) {
        setUnderProcessPageSize(restore);
      }
    }
    // Only react to filter changes; do not reset on pageSize changes
    // This prevents immediate override back to 10 after user selects 100
  }, [embridary_placement_front_filter]);

  const {
    data: top_cards,
    isLoading: topcard_loading,
  } = useFetchTopCardCountQuery({ refetchOnMountOrArgChange: true });

  const {
    data: client_company,
    isLoading: clientCompany_loading,
  } = useGetCompanyNameFromJobcardQuery({ refetchOnMountOrArgChange: true });

  const {
    data: client_company_completed,
    isLoading: clientCompany_loading_completed,
  } = useGetCompanyNameFromJobcardCompletedQuery({
    refetchOnMountOrArgChange: true,
  });

  const [tableCards, setTableCards] = React.useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!cards || !Array.isArray(cards.results)) return;
    if (!role) return;

    const roleStageMap = {
      [ROLES.CUTTING]: STAGES.CUTTING,
      [ROLES.SECONDARY_PROCESS]: STAGES.SECONDARY_PROCESS,
      [ROLES.EMBROIDARY]: STAGES.EMBROIDARY,
      [ROLES.PRODUCTION]: STAGES.PRODUCTION,
      [ROLES.PACKING]: STAGES.PACKING,
      [ROLES.ACCOUNT]: STAGES.ACCOUNT,
      [ROLES.PAYMENT]: STAGES.PAYMENT,
      [ROLES.DISPATCH]: STAGES.DISPATCH,
    };

    const targetStage = roleStageMap[role];

    if (targetStage) {
      setTableCards(cards.results.filter((card) => card.current_stage === targetStage));
    } else {
      setTableCards(cards.results);
    }
  }, [cards, role]);

  const underProcessCards = useMemo(() => {
    const base = Array.isArray(tableCards) ? tableCards : [];
    const filtered = base.filter((card) => card?.current_stage !== STAGES.COMPLETED);

    if (embridary_placement_front_filter) {
      const q = String(embridary_placement_front_filter).toLowerCase();
      return filtered.filter((card) => String(card?.embr_Front || "").toLowerCase().includes(q));
    }

    return filtered;
  }, [tableCards, embridary_placement_front_filter]);

  // Completed cards: frontend-sorted to avoid any backend "priority-first" ordering
  const completedCards = useMemo(() => {
    const results = Array.isArray(cardsCompleted?.results) ? cardsCompleted.results : [];

    const filtered = results.filter((card) => {
      // First filter by completed stage
      if (card?.current_stage !== STAGES.COMPLETED) return false;

      // Then filter by embroidary placement front if filter is set (substring, case-insensitive)
      if (embridary_placement_front_filter_completed) {
        const q = String(embridary_placement_front_filter_completed).toLowerCase();
        const v = String(card?.embr_Front || "").toLowerCase();
        return v.includes(q);
      }

      return true;
    });

    const sortKey = sort_job_completed || "id";

    // Determine if sort is descending (starts with "-")
    const isDescending = String(sortKey).startsWith("-");
    const columnName = isDescending ? String(sortKey).substring(1) : String(sortKey);

    const toTime = (value) => {
      if (value == null) return 0;
      if (value instanceof Date) {
        const t = value.getTime();
        return Number.isFinite(t) ? t : 0;
      }
      if (typeof value === "number") return Number.isFinite(value) ? value : 0;

      const s = String(value).trim();
      if (!s) return 0;

      // Handle common backend format: "DD/MM/YYYY" (optionally with time)
      const slash = s.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
      );
      if (slash) {
        const dd = Number(slash[1]);
        const mm = Number(slash[2]);
        const yyyy = Number(slash[3]);
        const hh = Number(slash[4] ?? 0);
        const mi = Number(slash[5] ?? 0);
        const ss = Number(slash[6] ?? 0);
        const t = new Date(yyyy, mm - 1, dd, hh, mi, ss).getTime();
        return Number.isFinite(t) ? t : 0;
      }

      // Handle "DD-MM-YYYY" (optionally with time)
      const dash = s.match(
        /^(\d{1,2})-(\d{1,2})-(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
      );
      if (dash) {
        const dd = Number(dash[1]);
        const mm = Number(dash[2]);
        const yyyy = Number(dash[3]);
        const hh = Number(dash[4] ?? 0);
        const mi = Number(dash[5] ?? 0);
        const ss = Number(dash[6] ?? 0);
        const t = new Date(yyyy, mm - 1, dd, hh, mi, ss).getTime();
        return Number.isFinite(t) ? t : 0;
      }

      // ISO / RFC / other parseable formats
      const t = Date.parse(s);
      return Number.isFinite(t) ? t : 0;
    };

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (columnName === "id") {
        const ai = Number(a?.id) || 0;
        const bi = Number(b?.id) || 0;
        comparison = ai - bi;
      } else if (columnName === "date") {
        const at = toTime(a?.date);
        const bt = toTime(b?.date);
        comparison = at - bt;
        // Tiebreaker: use id
        if (comparison === 0) {
          comparison = (Number(a?.id) || 0) - (Number(b?.id) || 0);
        }
      } else if (columnName === "deliver_date") {
        const at = toTime(a?.deliver_date);
        const bt = toTime(b?.deliver_date);
        comparison = at - bt;
        // Tiebreaker: use id
        if (comparison === 0) {
          comparison = (Number(a?.id) || 0) - (Number(b?.id) || 0);
        }
      } else {
        // Fallback: string compare on the chosen key, then id
        const av = String(a?.[columnName] ?? "");
        const bv = String(b?.[columnName] ?? "");
        comparison = av.localeCompare(bv);
        if (comparison === 0) {
          comparison = (Number(a?.id) || 0) - (Number(b?.id) || 0);
        }
      }

      // Reverse comparison if descending
      return isDescending ? -comparison : comparison;
    });

    return sorted;
  }, [cardsCompleted, embridary_placement_front_filter_completed, sort_job_completed]);

  // console.log("completedcards", completedCards);

  const showCountcm = () =>
    [ROLES.ADMIN, ROLES.DESIGN, ROLES.ACCOUNT, ROLES.VIEWER].includes(role);

  const showCountComp = () => showCountcm();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [recordBig, setRecordBig] = useState([]);
  const formattedHatData = useMemo(
    () => formatHatDataToJSON(recordBig),
    [recordBig]
  );

  // Calculate selected quantity from recordBig
  const selectedQty = useMemo(() => {
    if (!recordBig || !Array.isArray(recordBig)) {
      return 0;
    }
    return recordBig.reduce((sum, record) => {
      const qty = record?.quantity;
      return sum + (typeof qty === 'number' ? qty : 0);
    }, 0);
  }, [recordBig]);

  // For easier understanding / debugging: consolidated JSON view of styles, colors, and parts
  if (recordBig && recordBig.length > 0) {
    console.log("final data:", formattedHatData);
  }
  const [generateShow, setGenerateShow] = useState(false);
  const [dispatchButtonConfig, setDispatchButtonConfig] = useState({
    visible: false,
    disabled: true,
    onClick: null,
  });

  const handleRefreshFilters = () => {
    refetchJobcards();
    refetchCompletedCards();
  };

  const performCancel = () => {
    setGenerateShow(false);
  };

  // console.log("recordBig", getUniqueColors2(recordBig));
  // console.log("recordBig", recordBig);
  // console.log(getUniqueColors(getManufatureDataSet(recordBig)));

  return (
    <Style>
      {/* Commented out ShowCount component - removed stage count display from jobcard page */}
      {/* {showCountComp() && (
        <div className="countstage">
          {topcard_loading ? <>loading</> : <ShowCount cards={top_cards} />}
        </div>
      )} */}

      <CardStyle>
        <>
          <div className="title">Under Processing</div>
          <div style={{ display: "flex", width: "100%" }}>
            {(role === ROLES.ADMIN || role === ROLES.DESIGN) && (
              <Button
                style={{
                  background: "var(--color-primary)",
                  color: "var(--light)",
                  fontSize: "1.2rem",
                  marginLeft: "10px",
                  padding: ".1rem 2rem",
                  borderRadius: ".3rem",
                  width: "fit-content",
                }}
                type="default"
                onClick={() => navigate("/create")}
              >
                Create
              </Button>
            )}
            <MultidropDown c="embridary_placement_front_filter" />
            <MultidropDown c="jobid_filter" />
            <MultidropDown client_company={client_company} />
            <MultidropDown cards={cards} />
            <MultidropDown c="created_by_filter" /> {/* Add created_by dropdown */}
            <MultidropDown c="date_filter" /> {/* Add date picker */}
            <Tooltip title="Refresh">
              <Button
                size="large"
                type="text"
                icon={
                  <ReloadOutlined
                    style={{
                      fontSize: "20px",
                      color: "#1677ff",
                    }}
                  />
                }
                onClick={handleRefreshFilters}
                loading={fetchJob || loadingCompleted}
                className="refresh-button"
                style={{
                  height: 40,
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Tooltip>
            <Tooltip title="Export Under Processing">
              <Button
                size="large"
                type="text"
                icon={<FileExcelOutlined style={{ fontSize: "20px", color: "#52c41a" }} />}
                onClick={handleExportUnderProcess}
                style={{
                  height: 40,
                  padding: "0 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </Tooltip>
            <CSVLink
              ref={csvLinkUnderProcessRef}
              data={prepareCSVData(underProcessCards, clients)}
              headers={csvHeaders}
              filename={`under_processing_jobcards_${moment().format("YYYY-MM-DD")}.csv`}
              style={{ display: 'none' }}
            />
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              marginTop: "10px",
              alignItems: "center",
              position: "sticky",
              top: 0,
              // Keep below sidebar and below any modal overlays
              zIndex: 1,
              background: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(6px)",
              padding: "6px 0",
            }}
          >
            <div>
              <AscDesc sort={"noncompleted"} />
            </div>
            <div>
              {/* priority / delivery schedule with Analyze Quantity button inside */}
              <PriorityInfo
                dispatchButtonConfig={dispatchButtonConfig}
                analyzeQuantityConfig={{
                  role,
                  recordBig,
                  setGenerateShow,
                  setRecordBig,
                  setSelectedRowKeys
                }}
              />
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <CardsTable
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                recordBig={recordBig}
                setRecordBig={setRecordBig}
                count={cards?.count}
                tableKey={"under_processing_dashboard"}
                cards={underProcessCards}
                page={"jobcard_page"}
                client_company={client_company}
                fetchJob={fetchJob}
                pageSize={underProcessPageSize}
                onPageSizeChange={setUnderProcessPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                user={user}
                onDispatchButtonStateChange={setDispatchButtonConfig}
              />
            </>
          )}
        </>
      </CardStyle>
      {(role == ROLES.ADMIN || role == ROLES.DESIGN || role === ROLES.VIEWER) && (
        <>
          <CardStyle>
            <>
              <div className="title">Completed</div>
              <div style={{ display: "flex", width: "100%" }}>
                <MultidropDown
                  client_company={client_company_completed}
                  c={"client_company_completed"}
                />
                <MultidropDown c="jobid_filter_completed" />
                <MultidropDown
                  c="embridary_placement_front_filter_completed"
                  cardsCompleted={cardsCompleted}
                />
                <Tooltip title="Export Completed">
                  <Button
                    size="large"
                    type="text"
                    icon={<FileExcelOutlined style={{ fontSize: "20px", color: "#52c41a" }} />}
                    onClick={handleExportCompleted}
                    style={{
                      height: 40,
                      padding: "0 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  />
                </Tooltip>
                <CSVLink
                  ref={csvLinkCompletedRef}
                  data={prepareCSVData(completedCards, clients)}
                  headers={csvHeaders}
                  filename={`completed_jobcards_${moment().format("YYYY-MM-DD")}.csv`}
                  style={{ display: 'none' }}
                />
              </div>
              <div style={{ width: "100%" }}>
                <AscDesc sort={"completed"} />
              </div>
              <CardsTable
                tableKey={"complted_dashboard"}
                cards={completedCards}
                count={cardsCompleted?.count}
                isCompleted
                page={"jobcard_page_complete"}
                client_company={client_company_completed}
                fetchJob={loadingCompleted}
                pageSize={completedPageSize}
                onPageSizeChange={setCompletedPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                user={user}
              />
            </>
          </CardStyle>
        </>
      )}
      {generateShow ? (
        <GenerateModel
          stylesData={formattedHatData?.styles || []}
          performCancel={performCancel}
          handlePrint={handlePrint}
          componentRef={componentRef}
          selectedQty={selectedQty}
        />
      ) : null}
    </Style>
  );
};

export default CardListing;

const Style = styled.div`
  height: 100%;
  min-height: 60vh;
  .refresh-button {
    transition: all 0.3s ease;
  }
  .refresh-button:hover {
    background: rgba(255, 255, 255, 0.7) !important;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .refresh-button .anticon {
    color: #1677ff !important;
  }
  /* Hat inventory table styling inside the generate modal */
  .hat-inventory-container {
    max-width: 100%;
    padding: 10px 14px 22px;
    background-color: #f5f5f5;
  }

  .hat-inventory-title-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px;
  }

  .hat-inventory-title {
    text-align: center;
    margin: 0;
    color: #333333;
    font-size: 24px;
    font-weight: 600;
    flex: 1;
  }

  .selected-qty-badge {
    background-color: var(--color-primary, #233872);
    color: var(--light, #ffffff);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    margin-left: 16px;
  }

  .hat-inventory-container .style-section {
    background: #ffffff;
    border-radius: 8px;
    padding: 5px 5px 5px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  }

  .hat-inventory-container .style-title {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 2px;
    color: #2c3e50;
    text-transform: capitalize;
  }

  .hat-inventory-container .hat-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 8px;
    background-color: #ffffff;
  }

  .hat-inventory-container .hat-table th {
    background-color: #3498db;
    color: #ffffff;
    padding: 2px 2px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
  }

  .hat-inventory-container .hat-table td {
    padding: 2px 2px;
    border-bottom: 1px solid #e2e8f0;
    font-size: 14px;
  }

  .hat-inventory-container .hat-table tr:hover td {
    background-color: #f8f9fa;
  }

  .hat-inventory-container .color-header {
    background-color: #ecf0f1;
    font-weight: 600;
    color: #2c3e50;
  }

  .hat-inventory-container .color-last-row td {
    border-bottom: 2px solid #3498db;
  }

  .hat-inventory-container .total-row {
    font-weight: 700;
    background-color: #e8f4f8;
    white-space: nowrap;
  }

  .hat-inventory-container .color-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 999px;
    color: #ffffff;
    font-weight: 600;
    font-size: 13px;
    text-transform: capitalize;
  }

  .hat-inventory-container .color-red {
    background-color: #e74c3c;
  }

  .hat-inventory-container .color-blue {
    background-color: #3498db;
  }

  .hat-inventory-container .color-orange {
    background-color: #e67e22;
  }

  .hat-inventory-container .color-green {
    background-color: #27ae60;
  }

  /* Newly added color classes */
  .hat-inventory-container .color-black {
    background-color: #111827;
  }

  .hat-inventory-container .color-yellow {
    background-color: #facc15;
    color: #1f2933;
  }

  .hat-inventory-container .color-pink {
    background-color: #ec4899;
  }

  /* Default color when no specific mapping is found */
  .hat-inventory-container .color-default {
    background-color: #6b7280;
  }
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    font-weight: 600;
    width: 400px;
    height: 400px;
    margin: auto;
    margin-top: 4rem;
    background: url(${process.env.PUBLIC_URL}/images/nothing.png) no-repeat;
    background-size: contain;
  }
`;

const StyleCard = styled.div`
  background: var(--light, #ffffff);
  border-radius: 16px;
  border: 1px solid rgba(55, 65, 81, 0.18);
  padding: 24px 28px;
  margin-bottom: 24px;
  box-shadow: 0 15px 35px rgba(17, 24, 39, 0.08);
`;

const StyleHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
`;

const StyleTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #111827;
  letter-spacing: 0.5px;
`;

const StyleLabel = styled.span`
  display: inline-block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(17, 24, 39, 0.6);
`;

const StyleMeta = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-primary, #233872);
`;

const TotalQuantityText = styled.span`
  display: block;
  margin-bottom: 16px;
  font-weight: 600;
  color: var(--color-primary, #233872);
  font-size: 1rem;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const ColorCard = styled.div`
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(35, 56, 114, 0.08);
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 12px 32px rgba(20, 36, 74, 0.08);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 45px rgba(20, 36, 74, 0.16);
  }
`;

const ColorHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: var(--color-primary, #233872);
  font-size: 1rem;
  z-index: 1;
`;

const ColorName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
`;

const ColorBadge = styled.div`
  background: var(--color-primary--light, rgba(35, 56, 114, 0.12));
  border: 1px solid rgba(35, 56, 114, 0.18);
  padding: 8px 16px;
  border-radius: 14px;
  font-size: 0.85rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  color: var(--color-primary, #233872);
`;

const ColorBadgeLabel = styled.span`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--color-primary, #233872);
`;

const ColorBadgeValue = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const SideList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  z-index: 1;
`;

const SideItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(35, 56, 114, 0.08);
  box-shadow: inset 0 0 0 1px rgba(35, 56, 114, 0.04);
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: rgba(35, 56, 114, 0.04);
    border-color: rgba(35, 56, 114, 0.2);
  }
`;

const SideLabel = styled.span`
  font-weight: 600;
  color: #4b5563;
  letter-spacing: 0.3px;
`;

const SideQuantity = styled.span`
  font-weight: 700;
  color: var(--color-primary, #233872);
  font-size: 1rem;
`;